import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { getDataAPIAuth } from "../../utils/API";
import { refreshToken } from "../../redux/actions/authAction";
import Modal from "@material-ui/core/Modal";
import { dialog } from "../Common/TreasuryDialog";
import { SARAmount, formatSAR } from "../../utils/currency";



/* ─── mobile breakpoint hook ─────────────────────────── */
const useIsMobile = (bp = 768) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
};

/* ─── helpers ─────────────────────────────────────────── */
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// ACTION_LABELS are now dynamically built using t() inside the component
const ACTION_LABELS_STATIC = {
  buy: { color: "#0e3725", bg: "#e8f4ee" },
  sell: { color: "#b94040", bg: "#fdeaea" },
  transfer: { color: "#7a6a2e", bg: "#fdf8e8" },
  dividend: { color: "#7a4e2d", bg: "#fdf3e7" },
};

const STATUS_COLORS = {
  pending: { bg: "#fef9c3", color: "#854d0e" },
  approved: { bg: "#dcfce7", color: "#166534" },
  rejected: { bg: "#fee2e2", color: "#991b1b" },
};

/* ─── colours / tokens ────────────────────────────────── */
const C = {
  bg: "#f5f4f0",
  card: "#ffffff",
  green: "#0e3725",
  gold: "#c9a84c",
  border: "#e8e6de",
  muted: "#8a8a7e",
  text: "#1a1a14",
};

const card = {
  background: C.card,
  borderRadius: "14px",
  border: `1px solid ${C.border}`,
  padding: "28px 28px 24px",
  marginBottom: "24px",
};

const sectionTitle = {
  fontSize: "11px",
  fontWeight: "700",
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  color: C.muted,
  marginBottom: "18px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

/* ─── icon helpers ────────────────────────────────────── */
const Icon = ({ d, size = 18, color = C.green }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ICONS = {
  wallet: "M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-1M16 12a2 2 0 1 0 4 0 2 2 0 0 0-4 0z",
  holdings: "M3 3h18v4H3zM3 10h18v4H3zM3 17h18v4H3z",
  invoice: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8",
  history: "M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z",
  download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3",
  filter: "M22 3H2l8 9.46V19l4 2v-8.54L22 3z",
  add: "M12 5v14M5 12h14",
  arrowUp: "M7 17l10-10M7 7h10v10",
  arrowDown: "M17 7l-10 10M17 17H7V7",
  withdraw: "M12 2v20M2 12h20",
};

/* ═══════════════════════════════════════════════════════ */
const Portfolio = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const dispatch = useDispatch();
  useEffect(() => { dispatch(refreshToken()); }, [dispatch]);

  const isMobile = useIsMobile();
  const { auth } = useSelector((s) => s);

  /* ── portfolio data ─────────────────────────────────── */
  const [balance, setBalance] = useState([]);
  const [transaction, setTransaction] = useState([]);
  const [totalHoldings, setTotalHoldings] = useState(0);
  const [liveCashBalance, setLiveCashBalance] = useState(null);

  /* ── withdrawal data ────────────────────────────────── */
  const [iban, setIban] = useState("");
  const [bankName, setBankName] = useState("");
  const [withdrawals, setWithdrawals] = useState([]);
  const [cashTransfers, setCashTransfers] = useState([]);
  const [wAmount, setWAmount] = useState("");
  const [wSubmitting, setWSubmitting] = useState(false);
  const [wLoading, setWLoading] = useState(false);

  /* ── exchange orders ──────────────────────────────── */
  const [orders, setOrders] = useState([]);

  /* ── invoice data ──────────────────────────────────── */
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  /* ── UI state ───────────────────────────────────────── */
  const [activeTab, setActiveTab] = useState("holdings");
  const [addFundOpen, setAddFundOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [fundSubmitting, setFundSubmitting] = useState(false);

  /* ── tx filters ─────────────────────────────────────── */
  const [txType, setTxType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  /* ── load all data ──────────────────────────────────── */
  const loadWithdrawals = useCallback(async (token) => {
    if (!token) return;
    setWLoading(true);
    try {
      const [profileRes, wRes, ctRes] = await Promise.all([
        fetch("/api/user/profile", { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch("/api/withdrawal/my-requests", { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch("/api/user/cash-transfers", { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      const u = profileRes.user || profileRes;
      setIban(u.iban || "");
      setBankName(u.bankName || "");
      setWithdrawals(wRes.withdrawals || []);
      setCashTransfers(ctRes.transfers || []);
      // Also update live cash balance from profile
      if (u.totalBalance !== undefined) {
        setLiveCashBalance(u.totalBalance);
      }
    } catch (err) {
      console.error("loadWithdrawals error:", err);
    } finally {
      setWLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async (token) => {
    if (!token) return;
    setInvoicesLoading(true);
    try {
      const res = await fetch("/api/invoices/my", { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      setInvoices(res.invoices || []);
    } catch (err) {
      console.error("loadInvoices error:", err);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    async function load() {
      if (!auth.data) return;
      const token = auth.data.accesstoken || auth.data.access_token;;
      const [txRes, balRes, walletRes, ordersRes] = await Promise.all([
        getDataAPIAuth("/getUserTransaction/" + auth.data.user._id, token),
        getDataAPIAuth("/getUserBalance/" + auth.data.user._id, token),
        getDataAPIAuth("/getWalletBalance", token),
        getDataAPIAuth("/getUserOrders/" + auth.data.user._id, token),
      ]);
      setTransaction(txRes.data || []);
      setBalance(balRes.data || []);
      setOrders((ordersRes.data && ordersRes.data.orders) || []);
      let tb = 0;
      (balRes.data || []).forEach((b) => { tb += b.propertyId.tokenPrice * b.units; });
      setTotalHoldings(tb);
      // Always use live balance from DB — not stale Redux state
      if (walletRes && walletRes.data && walletRes.data.status === 1) {
        setLiveCashBalance(walletRes.data.totalBalance || 0);
      }
      // Load withdrawal data
      await loadWithdrawals(token);
    }
    load();
  }, [auth, loadWithdrawals]);

  const cashBalance = liveCashBalance !== null ? liveCashBalance : (auth.data?.user?.totalBalance || 0);
  const totalPortfolio = cashBalance + totalHoldings;

  const ACTION_LABELS = {
    buy: { label: t("portfolio.subscribe"), ...ACTION_LABELS_STATIC.buy },
    sell: { label: t("portfolio.sell"), ...ACTION_LABELS_STATIC.sell },
    transfer: { label: t("portfolio.transfer"), ...ACTION_LABELS_STATIC.transfer },
    dividend: { label: t("portfolio.distribution"), ...ACTION_LABELS_STATIC.dividend },
  };

  /* ── filtered transactions ──────────────────────────── */
  // Normalise cash transfers into the same shape as token transactions
  const normalisedCashTransfers = useMemo(() => {
    return (Array.isArray(cashTransfers) ? cashTransfers : []).map((ct) => ({
      ...ct,
      _isCashTransfer: true,
    }));
  }, [cashTransfers]);

  const filteredTx = useMemo(() => {
    const tokenTxs = (Array.isArray(transaction) ? transaction : []);
    const orderTxs = (Array.isArray(orders) ? orders : []).map(o => ({ ...o, _isOrder: true }));
    let combined;
    if (txType === "transfer") {
      combined = normalisedCashTransfers;
    } else if (txType === "orders") {
      // Orders tab: order-book orders + completed buy/sell token transactions
      const completedTrades = tokenTxs.filter((t) => t.action === "buy" || t.action === "sell");
      combined = [...completedTrades, ...orderTxs].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    } else if (txType === "dividend") {
      combined = tokenTxs.filter((t) => t.action === "dividend");
    } else if (txType === "all") {
      // Merge all: token txs + cash transfers + orders, sorted by date
      combined = [...tokenTxs, ...normalisedCashTransfers, ...orderTxs].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    } else {
      // buy — subscriptions only (exclude dividends)
      combined = tokenTxs.filter((t) => t.action === txType && t.action !== "dividend");
    }
    return combined.filter((t) => {
      if (dateFrom && new Date(t.createdAt) < new Date(dateFrom)) return false;
      if (dateTo && new Date(t.createdAt) > new Date(dateTo + "T23:59:59")) return false;
      return true;
    });
  }, [transaction, normalisedCashTransfers, orders, txType, dateFrom, dateTo]);

  /* ── export helpers ─────────────────────────────────── */
  const exportCSV = () => {
    const rows = [
      ["Date", "Type", "Property", "Units", "Price (SAR)", "Total (SAR)"],
      ...filteredTx.map((t) => [
        new Date(t.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}),
        t.action === "buy" ? "Subscribe" : t.action === "sell" ? "Sell" : t.action === "dividend" ? "Distribution" : "Transfer",
        t.propertyId?.title || "",
        t.units || "",
        t.price || t.amount || 0,
        t._isCashTransfer ? (t.amount || 0) : (t.price * t.units || 0),
      ]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "treasury_transactions.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const win = window.open("", "_blank");
    const rows = filteredTx.map((t) => `
      <tr>
        <td>${new Date(t.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</td>
        <td>${t.action === "buy" ? "Subscribe" : t.action === "sell" ? "Sell" : t.action === "dividend" ? "Distribution" : "Transfer"}</td>
        <td>${t.propertyId?.title || ""}</td>
        <td style="text-align:center">${t.units}</td>
        <td style="text-align:right">﷼ ${fmt(t.price)}</td>
        <td style="text-align:right">﷼ ${fmt(t.price * t.units)}</td>
      </tr>`).join("");
    win.document.write(`<!DOCTYPE html><html><head><title>Treasury Transactions</title>
      <style>body{font-family:Arial,sans-serif;padding:40px;color:#1a1a14}h1{color:#0e3725;font-size:22px;margin-bottom:4px}p{color:#8a8a7e;font-size:13px;margin-bottom:24px}table{width:100%;border-collapse:collapse;font-size:13px}th{background:#0e3725;color:#fff;padding:10px 12px;text-align:left}td{padding:9px 12px;border-bottom:1px solid #e8e6de}tr:nth-child(even){background:#f9f8f5}.total{margin-top:16px;text-align:right;font-weight:700;color:#0e3725}</style>
      </head><body>
      <h1>Treasury Portfolio — Transaction History</h1>
      <p>Exported on ${new Date().toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</p>
      <table><thead><tr><th>Date</th><th>Type</th><th>Property</th><th style="text-align:center">Units</th><th style="text-align:right">Price</th><th style="text-align:right">Total</th></tr></thead><tbody>${rows}</tbody></table>
      <p class="total">Total: ﷼ ${fmt(filteredTx.reduce((s, t) => s + t.price * t.units, 0))}</p>
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  /* ── withdrawal submit ──────────────────────────────── */
  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(wAmount);
    if (!amt || amt <= 0) {
      dialog.error(t("dialog.validationTitle"), t("dialog.invalidAmount"));
      return;
    }
    if (amt > cashBalance) {
      dialog.error(t("dialog.insufficientBalanceTitle"), `${t("dialog.availableBalance")} ${formatSAR(cashBalance, 2)}`);
      return;
    }
    if (!iban) {
      dialog.error(t("dialog.noBankTitle"), t("dialog.noBankMsg"));
      return;
    }
    const confirmed = await dialog.confirm(
      "Confirm Withdrawal",
      `Request withdrawal of ${formatSAR(amt, 2)} to:\n\nIBAN: ${iban}\nBank: ${bankName}\n\nThis amount will be held pending admin approval.`
    );
    if (!confirmed) return;
    setWSubmitting(true);
    const token = auth?.data?.accesstoken || auth?.data?.access_token;
    try {
      const res = await fetch("/api/withdrawal/request", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amt }),
      });
      const data = await res.json();
      if (data.status === 1) {
        dialog.success(t("dialog.requestSubmittedTitle"), data.msg);
        setWAmount("");
        await loadWithdrawals(token);
        // Refresh live balance
        const walletRes = await getDataAPIAuth("/getWalletBalance", token);
        if (walletRes && walletRes.data && walletRes.data.status === 1) {
          setLiveCashBalance(walletRes.data.totalBalance || 0);
        }
      } else {
        dialog.error(t("dialog.errorTitle"), data.msg || t("dialog.withdrawalFailed"));
      }
    } catch {
      dialog.error("Error", "Network error. Please try again.");
    } finally {
      setWSubmitting(false);
    }
  };

  /* ── add fund modal ─────────────────────────────────── */
  const addFundSubmit = async (e) => {
    e.preventDefault();
    if (fundSubmitting) return;
    const { amount } = e.target.elements;
    if (!image) { dialog.error(t("dialog.missingReceiptTitle"), t("dialog.missingReceiptMsg")); return; }
    setFundSubmitting(true);
    const formData = new FormData();
    formData.append("file", image);
    formData.append("userId", auth.data?.user?._id || "");
    formData.append("amount", amount.value);
    const token = auth?.data?.accesstoken || auth?.data?.access_token;
    try {
      const res = await axios.post("/api/requestFund", formData, {
        headers: { "Content-Type": "multipart/form-data", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.data.status == 1) {
        setAddFundOpen(false);
        setImage(null);
        dialog.success(t("dialog.requestSentTitle"), t("dialog.requestSentMsg"));
      } else {
        dialog.error(t("dialog.errorTitle"), res.data.msg || t("dialog.requestFailed"));
      }
    } catch (err) {
      dialog.error(t("dialog.errorTitle"), err?.response?.data?.msg || t("dialog.requestFailed"));
    } finally {
      setFundSubmitting(false);
    }
  };

  /* ── render ─────────────────────────────────────────── */
  return (
    <div style={{ minHeight: "100vh", background: C.bg, paddingTop: "40px", paddingBottom: "60px" }}>
      <div style={{ maxWidth: "980px", margin: "0 auto", padding: "0 24px" }}>

        {/* ── Page header ── */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "32px", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: "700", color: C.green, margin: "0 0 4px", letterSpacing: "-0.3px" }}>{t("portfolio.title")}</h1>
            <p style={{ fontSize: "14px", color: C.muted, margin: 0 }}>{t("portfolio.subtitle")}</p>
          </div>
          <button
            onClick={() => setAddFundOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: "8px", background: C.gold, color: "#fff", border: "none", borderRadius: "10px", padding: "11px 22px", fontSize: "14px", fontWeight: "600", cursor: "pointer", boxShadow: "0 4px 16px rgba(201,168,76,0.35)" }}
          >
            <Icon d={ICONS.add} size={16} color="#fff" />
            {t("portfolio.addFunds")}
          </button>
        </div>

        {/* ── Summary row ── */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "16px", marginBottom: "28px" }}>
          {[
            { label: t("portfolio.cashBalance"), value: cashBalance, icon: ICONS.wallet, accent: C.gold },
            { label: t("portfolio.holdingsValue"), value: totalHoldings, icon: ICONS.holdings, accent: C.green },
            { label: t("portfolio.totalPortfolio"), value: totalPortfolio, icon: ICONS.history, accent: "#4a7c5e" },
          ].map((item) => (
            <div key={item.label} style={{ background: C.card, borderRadius: "14px", border: `1px solid ${C.border}`, padding: "22px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "1px", textTransform: "uppercase", color: C.muted }}>{item.label}</span>
                <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: `${item.accent}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon d={item.icon} size={17} color={item.accent} />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <SARAmount value={item.value} decimals={2} decimalSize="0.65em" style={{ fontSize: "26px", fontWeight: "700", color: C.text, letterSpacing: "-0.5px" }} />
              </div>
            </div>
          ))}
        </div>

        {/* ── Tab bar ── */}
        <div style={{ display: "flex", flexWrap: isMobile ? "wrap" : "nowrap", gap: "4px", marginBottom: "20px", background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, padding: "6px" }}>
          {[
            { key: "holdings", label: t("portfolio.holdings"), icon: ICONS.holdings },
            { key: "transactions", label: t("portfolio.transactions"), icon: ICONS.history },
            { key: "withdrawals", label: t("portfolio.withdrawals"), icon: ICONS.withdraw },
            { key: "invoices", label: t("portfolio.invoices"), icon: ICONS.invoice },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
              setActiveTab(tab.key);
              if (tab.key === "invoices" && invoices.length === 0) {
                const token = auth.data?.accesstoken || auth.data?.access_token;
                loadInvoices(token);
              }
            }}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
                padding: "10px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer", border: "none",
                background: activeTab === tab.key ? C.green : "transparent",
                color: activeTab === tab.key ? "#fff" : C.muted,
                transition: "all 0.15s",
              }}
            >
              <Icon d={tab.icon} size={14} color={activeTab === tab.key ? "#fff" : C.muted} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ══════════ TAB: HOLDINGS ══════════ */}
        {activeTab === "holdings" && (
          <div style={card}>
            <div style={sectionTitle}>
              <Icon d={ICONS.holdings} size={15} color={C.muted} />
              {t("portfolio.tokenHoldings")}
            </div>
            {balance.filter(b => b.units > 0).length === 0 ? (
              <p style={{ color: C.muted, fontSize: "14px", textAlign: "center", padding: "24px 0" }}>{t("portfolio.noHoldings")}</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {[t("portfolio.colProperty"), t("portfolio.colTokens"), t("portfolio.colTokenPrice"), t("portfolio.colTotalValue"), ""].map((h) => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: h === "" ? "center" : (isAr ? "right" : "left"), fontSize: "11px", fontWeight: "700", letterSpacing: "0.8px", textTransform: "uppercase", color: C.muted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {balance.filter(b => b.units > 0).map((b, i) => (
                      <tr
                        key={i}
                        onClick={() => window.location.href = `/dashboard/tradeproperty/${b.propertyId._id}`}
                        style={{ borderBottom: `1px solid ${C.border}`, cursor: "pointer", transition: "background 0.12s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f5f4f0"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: "14px 12px", fontWeight: "600", color: C.text }}>{b.propertyId.title}</td>
                        <td style={{ padding: "14px 12px", color: C.muted }}>{fmt(b.units)} {t("portfolio.tokens")}</td>
                        <td style={{ padding: "14px 12px", color: C.muted }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                            <SARAmount value={b.propertyId.tokenPrice} decimals={2} />
                          </span>
                        </td>
                        <td style={{ padding: "14px 12px", textAlign: "right", fontWeight: "700", color: C.green }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
                            <SARAmount value={b.propertyId.tokenPrice * b.units} decimals={2} />
                          </span>
                        </td>
                        <td style={{ padding: "14px 12px", textAlign: "center" }}>
                          <a
                            href={`/dashboard/tradeproperty/${b.propertyId._id}`}
                            onClick={e => e.stopPropagation()}
                            style={{ fontSize: "12px", color: C.gold, fontWeight: "600", textDecoration: "none", background: `${C.gold}18`, padding: "5px 12px", borderRadius: "20px", whiteSpace: "nowrap" }}
                          >
                            {t("portfolio.trade") || "Trade"} →
                          </a>
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: "#f9f8f5" }}>
                      <td colSpan={3} style={{ padding: "12px 12px", fontSize: "12px", fontWeight: "700", letterSpacing: "0.8px", textTransform: "uppercase", color: C.muted }}>{t("portfolio.totalHoldings")}</td>
                      <td style={{ padding: "12px 12px", textAlign: "right", fontWeight: "700", color: C.green, fontSize: "16px" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
                          <SARAmount value={totalHoldings} decimals={2} />
                        </span>
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════ TAB: TRANSACTIONS ══════════ */}
        {activeTab === "transactions" && (
          <div style={card}>
            {/* header row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
              <div style={sectionTitle}>
                <Icon d={ICONS.history} size={15} color={C.muted} />
                {t("portfolio.transactionHistory")}
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={exportCSV} style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: `1.5px solid ${C.border}`, borderRadius: "8px", padding: "7px 14px", fontSize: "12px", fontWeight: "600", color: C.muted, cursor: "pointer" }}>
                  <Icon d={ICONS.download} size={13} color={C.muted} /> Excel
                </button>
                <button onClick={exportPDF} style={{ display: "flex", alignItems: "center", gap: "6px", background: C.green, border: "none", borderRadius: "8px", padding: "7px 14px", fontSize: "12px", fontWeight: "600", color: "#fff", cursor: "pointer" }}>
                  <Icon d={ICONS.download} size={13} color="#fff" /> PDF
                </button>
              </div>
            </div>

            {/* filters */}
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "20px", padding: "16px", background: "#f9f8f5", borderRadius: "10px", border: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {[
                  { key: "all", label: t("portfolio.all") },
                  { key: "buy", label: t("portfolio.subscriptions") },
                  { key: "dividend", label: t("portfolio.distributions") },
                  { key: "orders", label: t("portfolio.orders") },
                  { key: "transfer", label: t("portfolio.transfers") },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setTxType(opt.key)}
                    style={{
                      padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", cursor: "pointer", border: "none",
                      background: txType === opt.key ? C.green : C.card,
                      color: txType === opt.key ? "#fff" : C.muted,
                      boxShadow: txType === opt.key ? "0 2px 8px rgba(14,55,37,0.15)" : "none",
                      transition: "all 0.15s",
                    }}
                  >{opt.label}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginLeft: isAr ? 0 : (isMobile ? 0 : "auto"), marginRight: isAr ? (isMobile ? 0 : "auto") : 0, width: isMobile ? "100%" : "auto" }}>
                <span style={{ fontSize: "12px", color: C.muted, fontWeight: "600" }}>{t("portfolio.from")}</span>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "12px", color: C.text, background: C.card, outline: "none" }} />
                <span style={{ fontSize: "12px", color: C.muted, fontWeight: "600" }}>{t("portfolio.to")}</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "12px", color: C.text, background: C.card, outline: "none" }} />
                {(dateFrom || dateTo || txType !== "all") && (
                  <button onClick={() => { setDateFrom(""); setDateTo(""); setTxType("all"); }}
                    style={{ fontSize: "11px", color: "#b94040", fontWeight: "600", background: "none", border: "none", cursor: "pointer" }}>{t("common.clear")}</button>
                )}
              </div>
            </div>

            {/* table */}
            {filteredTx.length === 0 ? (
              <p style={{ color: C.muted, fontSize: "14px", textAlign: "center", padding: "24px 0" }}>{t("portfolio.noTransactions")}</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {[t("portfolio.colDate"), t("portfolio.colType"), t("portfolio.colProperty"), t("portfolio.colUnits"), t("portfolio.colPrice"), t("portfolio.colTotal"), t("portfolio.colStatus")].map((h) => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: isAr ? "right" : "left", fontSize: "11px", fontWeight: "700", letterSpacing: "0.8px", textTransform: "uppercase", color: C.muted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTx.map((tx, i) => {
                      // In "all" tab, insert a group header row when the type changes
                      const prevT = filteredTx[i - 1];
                      const getGroup = (tx) => tx._isOrder ? "orders" : tx._isCashTransfer ? "transfer" : tx.action;
                        const groupLabel = { buy: t("portfolio.subscriptions"), sell: t("portfolio.sells"), dividend: t("portfolio.distributions"), orders: t("portfolio.marketOrders"), transfer: t("portfolio.transfers") };
                      const showGroupHeader = txType === "all" && (i === 0 || getGroup(tx) !== getGroup(prevT));
                      const groupHeaderRow = showGroupHeader ? (
                        <tr key={`grp-${i}`}>
                          <td colSpan={7} style={{ padding: "10px 12px 6px", fontSize: "10px", fontWeight: "800", letterSpacing: "1.2px", textTransform: "uppercase", color: C.muted, background: "#f5f4f0", borderBottom: `1px solid ${C.border}` }}>
                            {groupLabel[getGroup(tx)] || getGroup(tx)}
                          </td>
                        </tr>
                      ) : null;
                      if (tx._isOrder) {
                        // Exchange order row (buy or sell order from the order book)
                        const isBuy = tx.action === "buy";
                        const orderMeta = isBuy
                          ? { label: t("portfolio.buyOrder"), color: "#166534", bg: "#dcfce7" }
                          : { label: t("portfolio.sellOrder"), color: "#b94040", bg: "#fdeaea" };
                        const orderStatus = tx.isOpen ? t("portfolio.open") : t("portfolio.filled");
                        const orderStatusStyle = tx.isOpen
                          ? { bg: "#fef9c3", color: "#854d0e" }
                          : { bg: "#dcfce7", color: "#166534" };
                        return (
                          <React.Fragment key={`ord-${i}`}>
                            {groupHeaderRow}
                          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                            <td style={{ padding: "13px 12px", color: C.muted, fontSize: "13px" }}>{new Date(tx.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</td>
                            <td style={{ padding: "13px 12px" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: orderMeta.bg, color: orderMeta.color, borderRadius: "20px", padding: "3px 10px", fontSize: "12px", fontWeight: "600" }}>
                                <Icon d={isBuy ? ICONS.arrowDown : ICONS.arrowUp} size={11} color={orderMeta.color} />
                                {orderMeta.label}
                              </span>
                            </td>
                            <td style={{ padding: "13px 12px", fontWeight: "600", color: C.text }}>{tx.propertyId?.title || "—"}</td>
                            <td style={{ padding: "13px 12px", color: C.muted }}>{fmt(tx.units)}</td>
                            <td style={{ padding: "13px 12px", textAlign: "right", color: C.muted }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
                                <SARAmount value={tx.price} decimals={2} />
                              </span>
                            </td>
                            <td style={{ padding: "13px 12px", textAlign: "right", fontWeight: "700", color: isBuy ? C.green : "#b94040" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
                                <SARAmount value={tx.price * tx.units} decimals={2} />
                              </span>
                            </td>
                            <td style={{ padding: "13px 12px" }}>
                              <span style={{ background: orderStatusStyle.bg, color: orderStatusStyle.color, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                                {orderStatus}
                              </span>
                            </td>
                          </tr>
                          </React.Fragment>
                        );
                      }
                      if (tx._isCashTransfer) {
                        // Cash transfer row (fund-in or withdrawal)
                        const isFundIn = tx.action === "fund_in";
                        const ctMeta = isFundIn
                          ? { label: t("portfolio.fundIn"), color: "#166534", bg: "#dcfce7" }
                          : { label: t("portfolio.withdrawal"), color: "#991b1b", bg: "#fee2e2" };
                        const statusColors = STATUS_COLORS[tx.status] || {};
                        return (
                          <React.Fragment key={`ct-${i}`}>
                            {groupHeaderRow}
                          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                            <td style={{ padding: "13px 12px", color: C.muted, fontSize: "13px" }}>{new Date(tx.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</td>
                            <td style={{ padding: "13px 12px" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: ctMeta.bg, color: ctMeta.color, borderRadius: "20px", padding: "3px 10px", fontSize: "12px", fontWeight: "600" }}>
                                <Icon d={isFundIn ? ICONS.arrowDown : ICONS.arrowUp} size={11} color={ctMeta.color} />
                                {ctMeta.label}
                              </span>
                            </td>
                            <td style={{ padding: "13px 12px", color: C.muted, fontSize: "13px" }}>
                              {isFundIn ? t("portfolio.bankTransfer") : (tx.bankName || t("portfolio.bankTransfer"))}
                            </td>
                            <td style={{ padding: "13px 12px", color: C.muted, fontSize: "13px" }}>—</td>
                            <td style={{ padding: "13px 12px", textAlign: "right" }}>
                              {tx.status && (
                                <span style={{ background: statusColors.bg, color: statusColors.color, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}>
                                  {tx.status}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: "13px 12px", textAlign: "right", fontWeight: "700", color: isFundIn ? "#166534" : "#991b1b" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
                                {isFundIn ? "+" : "-"}<SARAmount value={tx.amount} decimals={2} />
                              </span>
                            </td>
                            <td style={{ padding: "13px 12px" }}>—</td>
                          </tr>
                          </React.Fragment>
                        );
                      }
                      // Token transaction row (subscription, sell, or distribution)
                      const meta = ACTION_LABELS[tx.action] || ACTION_LABELS.buy;
                      const isDividend = tx.action === "dividend";
                      const isSell = tx.action === "sell";
                      // For dividends: price = total amount credited, units = tokens held at time
                      // Display: amount credited (price field), no per-unit price column
                      const totalAmt = isDividend ? tx.price : (tx.price * tx.units);
                      const iconPath = isDividend ? ICONS.wallet : (isSell ? ICONS.arrowUp : ICONS.arrowDown);
                      return (
                        <React.Fragment key={i}>
                          {groupHeaderRow}
                        <tr style={{ borderBottom: `1px solid ${C.border}`, background: isDividend ? "#fffdf8" : "transparent" }}>
                          <td style={{ padding: "13px 12px", color: C.muted, fontSize: "13px" }}>{new Date(tx.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</td>
                          <td style={{ padding: "13px 12px" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: meta.bg, color: meta.color, borderRadius: "20px", padding: "3px 10px", fontSize: "12px", fontWeight: "600" }}>
                              <Icon d={iconPath} size={11} color={meta.color} />
                              {meta.label}
                            </span>
                          </td>
                          <td style={{ padding: "13px 12px", fontWeight: "600", color: C.text }}>{tx.propertyId?.title || "—"}</td>
                          <td style={{ padding: "13px 12px", color: C.muted }}>
                            {isDividend ? (
                              <span style={{ fontSize: "12px", color: C.muted, fontStyle: "italic" }}>{fmt(tx.units)} {t("portfolio.tokensHeld")}</span>
                            ) : fmt(tx.units)}
                          </td>
                          <td style={{ padding: "13px 12px", textAlign: "right", color: C.muted }}>
                            {isDividend ? (
                              <span style={{ fontSize: "12px", color: C.muted }}>—</span>
                            ) : (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
                                <SARAmount value={tx.price} decimals={2} />
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "13px 12px", textAlign: "right", fontWeight: "700", color: isDividend ? "#7a4e2d" : (isSell ? "#b94040" : C.green) }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
                              {isDividend && <span style={{ fontSize: "11px", marginRight: "2px" }}>+</span>}
                              <SARAmount value={totalAmt} decimals={2} />
                            </span>
                          </td>
                          <td style={{ padding: "13px 12px" }}>
                            {isDividend ? (
                              <span style={{ background: "#fdf3e7", color: "#7a4e2d", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>Credited</span>
                            ) : (
                              <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>Filled</span>
                            )}
                          </td>
                        </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════ TAB: WITHDRAWALS ══════════ */}
        {activeTab === "withdrawals" && (
          <>
            {/* Bank info banner */}
            <div style={{
              background: `linear-gradient(135deg, ${C.green} 0%, #1a4d35 100%)`,
              borderRadius: 16, padding: "24px 28px", marginBottom: 20,
              display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", gap: isMobile ? 16 : 0, color: "#fff",
            }}>
              <div>
                <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4, letterSpacing: 1, textTransform: "uppercase" }}>{t("portfolio.availableBalance")}</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: C.gold }}>
                  <SARAmount value={cashBalance} decimals={2} decimalSize="0.65em" />
                </div>
              </div>
                          <div style={{ textAlign: isAr ? "right" : (isMobile ? "left" : "right") }}>
                <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>{t("portfolio.registeredBank")}</div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{bankName || "—"}</div>
                <div style={{ fontFamily: "monospace", fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                  {iban ? `${iban.substring(0, 6)}••••••••••••${iban.slice(-4)}` : t("portfolio.noIban")}
                </div>
                {!iban && (
                  <a href="/dashboard/profile" style={{ fontSize: 12, color: C.gold, marginTop: 6, display: "inline-block" }}>
                    {t("portfolio.addIbanInProfile")}
                  </a>
                )}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.6fr", gap: 20 }}>
              {/* Withdrawal form */}
              <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: "24px" }}>
                <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700, color: C.text }}>{t("portfolio.requestWithdrawal")}</h3>
                <p style={{ margin: "0 0 18px", fontSize: 13, color: C.muted }}>
                  {t("portfolio.withdrawalNote")}
                </p>
                {!iban ? (
                  <div style={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "14px 16px", fontSize: 13, color: "#92400e" }}>
                    {t("portfolio.noIbanWarning")}{" "}
                    <a href="/dashboard/profile" style={{ color: C.gold, fontWeight: 700 }}>{t("portfolio.profileLink")}</a>{" "}
                    {t("portfolio.noIbanWarning2")}
                  </div>
                ) : (
                  <form onSubmit={handleWithdrawalSubmit}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{t("portfolio.amountSar")}</label>
                    <input
                      type="number" min="1" max={cashBalance} step="0.01"
                      value={wAmount} onChange={(e) => setWAmount(e.target.value)}
                      placeholder={`Max: ${formatSAR(cashBalance, 2)}`}
                      style={{ width: "100%", padding: "11px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 15, fontWeight: 600, color: C.text, outline: "none", boxSizing: "border-box", marginBottom: 14 }}
                    />
                    {/* Quick pct buttons */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                      {[25, 50, 75, 100].map((pct) => (
                        <button key={pct} type="button"
                          onClick={() => setWAmount(((cashBalance * pct) / 100).toFixed(2))}
                          style={{ flex: 1, padding: "6px 0", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
                          {pct}%
                        </button>
                      ))}
                    </div>
                    <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: C.muted }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span>{t("portfolio.withdrawalTo")}:</span>
                        <strong style={{ color: C.text }}>{bankName}</strong>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>{t("portfolio.iban")}:</span>
                        <strong style={{ color: C.text, fontFamily: "monospace", fontSize: 12 }}>{iban}</strong>
                      </div>
                    </div>
                    <button type="submit" disabled={wSubmitting || !wAmount || parseFloat(wAmount) <= 0}
                      style={{
                        width: "100%", padding: "12px 0",
                        background: wSubmitting || !wAmount ? "#e5e7eb" : `linear-gradient(135deg, ${C.gold}, #7a5c10)`,
                        color: wSubmitting || !wAmount ? "#9ca3af" : "#fff",
                        border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700,
                        cursor: wSubmitting || !wAmount ? "not-allowed" : "pointer",
                      }}>
                      {wSubmitting ? t("common.submitting") : t("portfolio.requestWithdrawal")}                </button>
                  </form>
                )}
              </div>

              {/* Withdrawal history */}
              <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: "24px" }}>
                <h3 style={{ margin: "0 0 18px", fontSize: 15, fontWeight: 700, color: C.text }}>{t("portfolio.withdrawalHistory")}</h3>
                {wLoading ? (
                  <div style={{ textAlign: "center", color: C.muted, padding: "30px 0" }}>{t("common.loading")}</div>
                ) : withdrawals.length === 0 ? (
                  <div style={{ textAlign: "center", color: C.muted, padding: "30px 0", fontSize: 14 }}>{t("portfolio.noWithdrawals")}</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {withdrawals.map((w) => {
                      const sc = STATUS_COLORS[w.status] || {};
                      return (
                        <div key={w._id} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: C.gold }}><SARAmount value={w.amount} decimals={2} decimalSize="0.7em" /></div>
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                              {new Date(w.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})} · {w.bankName}
                            </div>
                            {w.adminNote && (
                              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{t("portfolio.note")}: {w.adminNote}</div>
                            )}
                          </div>
                          <span style={{ background: sc.bg, color: sc.color, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: "capitalize", flexShrink: 0 }}>
                            {w.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

      {/* ══════════ TAB: INVOICES ══════════ */}
        {activeTab === "invoices" && (
          <div style={card}>
            <div style={sectionTitle}>
              <Icon d={ICONS.invoice} size={15} color={C.muted} />
              {t("portfolio.myInvoices")}
            </div>
            {invoicesLoading ? (
              <div style={{ textAlign: "center", color: C.muted, padding: "30px 0" }}>{t("common.loading")}</div>
            ) : invoices.length === 0 ? (
              <p style={{ color: C.muted, fontSize: "14px", textAlign: "center", padding: "24px 0" }}>{t("portfolio.noInvoices")}</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {[t("portfolio.invoiceNum"), t("portfolio.colDate"), t("portfolio.colType"), t("portfolio.colProperty"), t("portfolio.subtotal"), t("portfolio.vat"), t("portfolio.colTotal"), ""].map((h) => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: isAr ? "right" : "left", fontSize: "11px", fontWeight: "700", letterSpacing: "0.8px", textTransform: "uppercase", color: C.muted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv._id} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: "13px 12px", fontWeight: "600", color: C.green, fontFamily: "monospace", fontSize: "12px" }}>{inv.invoiceNumber}</td>
                        <td style={{ padding: "13px 12px", color: C.muted, fontSize: "12px" }}>{new Date(inv.issueDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</td>
                        <td style={{ padding: "13px 12px" }}>
                          <span style={{ background: inv.type === "subscription" ? "#e8f4ee" : "#fdf8e8", color: inv.type === "subscription" ? C.green : "#7a6a2e", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}>
                            {inv.type}
                          </span>
                        </td>
                        <td style={{ padding: "13px 12px", color: C.text }}>{inv.propertyId?.title || "—"}</td>
                        <td style={{ padding: "13px 12px", textAlign: "right", color: C.muted }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}><SARAmount value={inv.subtotal} decimals={2} /></span>
                        </td>
                        <td style={{ padding: "13px 12px", textAlign: "right", color: C.muted }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}><SARAmount value={inv.vatAmount} decimals={2} /></span>
                        </td>
                        <td style={{ padding: "13px 12px", textAlign: "right", fontWeight: "700", color: C.green }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}><SARAmount value={inv.total} decimals={2} /></span>
                        </td>
                        <td style={{ padding: "13px 12px", textAlign: "center" }}>
                          <button
                            onClick={async () => {
                              const token = auth?.data?.accesstoken || auth?.data?.access_token;
                              const res = await fetch(`/api/invoices/${inv._id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
                              if (!res.ok) { alert(t("portfolio.pdfDownloadFailed")); return; }
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url; a.download = `${inv.invoiceNumber}.pdf`; a.click();
                              URL.revokeObjectURL(url);
                            }}
                            style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "#0e3725", color: "#c9a84c", border: "none", borderRadius: "7px", padding: "5px 12px", fontSize: "11px", fontWeight: "700", cursor: "pointer", whiteSpace: "nowrap" }}
                          >
                            <Icon d={ICONS.download} size={11} color="#c9a84c" /> PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ── Add Fund Modal ── */}
      <Modal open={addFundOpen} onClose={() => setAddFundOpen(false)}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: C.card, borderRadius: "16px", padding: "36px", width: "420px", maxWidth: "90vw", outline: "none" }}>
          <h2 style={{ fontSize: "20px", fontWeight: "700", color: C.green, margin: "0 0 6px" }}>{t("portfolio.addFunds")}</h2>
          <p style={{ fontSize: "13px", color: C.muted, margin: "0 0 24px" }}>{t("portfolio.addFundsNote")}</p>
          <form onSubmit={addFundSubmit}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: C.muted, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: "6px" }}>{t("portfolio.amountSar")}</label>
            <input name="amount" type="number" required placeholder="e.g. 10,000" step="0.01" min="0.01"
              style={{ width: "100%", padding: "11px 14px", border: `1.5px solid ${C.border}`, borderRadius: "10px", fontSize: "14px", color: C.text, background: "#fafaf8", outline: "none", boxSizing: "border-box", marginBottom: "16px" }} />
            <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: C.muted, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: "6px" }}>{t("portfolio.transferReceipt")}</label>
            <div style={{ border: `2px dashed ${C.border}`, borderRadius: "10px", padding: "20px", textAlign: "center", marginBottom: "20px", cursor: "pointer", background: "#fafaf8" }}
              onClick={() => document.getElementById("fund-file-input").click()}>
              {image ? (
                <p style={{ fontSize: "13px", color: C.green, fontWeight: "600", margin: 0 }}>✓ {image.name}</p>
              ) : (
                <p style={{ fontSize: "13px", color: C.muted, margin: 0 }}>{t("portfolio.uploadReceipt")}</p>
              )}
              <input id="fund-file-input" type="file" accept=".pdf,image/*" style={{ display: "none" }} onChange={(e) => setImage(e.target.files[0])} />
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button type="button" onClick={() => setAddFundOpen(false)}
                style={{ flex: 1, padding: "11px", border: `1.5px solid ${C.border}`, borderRadius: "10px", background: "none", fontSize: "14px", fontWeight: "600", color: C.muted, cursor: "pointer" }}>{t("common.cancel")}</button>
              <button type="submit" disabled={fundSubmitting}
                style={{ flex: 2, padding: "11px", border: "none", borderRadius: "10px", background: fundSubmitting ? "#c8b06a" : C.gold, color: "#fff", fontSize: "14px", fontWeight: "700", cursor: fundSubmitting ? "not-allowed" : "pointer", boxShadow: "0 4px 16px rgba(201,168,76,0.35)", opacity: fundSubmitting ? 0.7 : 1 }}>
                {fundSubmitting ? t("common.submitting") : t("common.submitRequest")}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default Portfolio;
